import React, { useState } from "react";
import {
  Check, X, Edit2, Trash2, Eye, EyeOff, Star, Play, Gift,
  ChevronDown, ChevronUp, Users, BookOpen, UserCog, Building2,
  HardDrive, MoreVertical,
} from "lucide-react";
import { AppBadge } from "../../ui";
import "./plans.scss";

const LIMITS = [
  { key: "maxStudentsPerBatch", label: "Students/Batch", icon: Users },
  { key: "maxBatches", label: "Batches", icon: BookOpen },
  { key: "maxCourses", label: "Courses", icon: BookOpen },
  { key: "maxInstructors", label: "Instructors", icon: UserCog },
  { key: "maxAdmins", label: "Admins", icon: Building2 },
  { key: "storageGB", label: "Storage", icon: HardDrive, suffix: " GB" },
  { key: "maxBranches", label: "Branches", icon: Building2 },
];

const MODULE_GROUPS = [
  {
    group: "Core Platform",
    keys: ["feeManagement", "enquiryManagement", "assignments", "quizAssessment"],
    labels: { feeManagement: "Fee Mgmt", enquiryManagement: "Enquiry", assignments: "Assignments", quizAssessment: "Quiz" },
  },
  {
    group: "AI & Practice",
    keys: ["aiMock", "aiLearning", "aiQuestionBank", "codingPractice"],
    labels: { aiMock: "AI Mock", aiLearning: "AI Learn", aiQuestionBank: "AI Q-Bank", codingPractice: "Coding" },
  },
  {
    group: "Integrations",
    keys: ["zoomV2", "recordings", "whatsapp", "interviews"],
    labels: { zoomV2: "Zoom", recordings: "Recordings", whatsapp: "WhatsApp", interviews: "Interviews" },
  },
  {
    group: "Advanced",
    keys: ["customBranding", "customDomain", "apiAccess", "enquiryFormBuilder", "secureVideoPlayer", "multiBranch"],
    labels: { customBranding: "Branding", customDomain: "Domain", apiAccess: "API", enquiryFormBuilder: "Form Builder", secureVideoPlayer: "Secure Video", multiBranch: "Branches" },
  },
];

const fmt = (amount) => {
  if (!amount || amount === 0) return "Free";
  return `₹${Number(amount).toLocaleString("en-IN")}`;
};

/**
 * PlanCard — A single plan card designed for both grid and list views.
 */
const PlanCard = ({
  plan, onEdit, onDelete, onToggleVisibility, onSetRecommended, onSetDefaultTrial, onSetFreePlan,
  isPopular = false, listMode = false,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const isPublic = plan.isPublic !== false;
  const isDefaultTrial = !!plan.isDefaultTrial;
  const isFree = !!plan.isFree;
  const enabledCount = MODULE_GROUPS.reduce(
    (sum, g) => sum + g.keys.filter((k) => plan[k]).length, 0
  );
  const totalModules = MODULE_GROUPS.reduce((s, g) => s + g.keys.length, 0);

  /* ── LIST MODE ── */
  if (listMode) {
    return (
      <div className={`plan-list-item ${isPopular ? "plan-list-item--popular" : ""} ${!isPublic ? "plan-list-item--private" : ""}`}>
        <div className="plan-list-item__main">
          <div className="plan-list-item__name-col">
            <h4 className="plan-list-item__name">{plan.name}</h4>
            <div className="plan-list-item__badges">
              {isPopular && <AppBadge color="blue" size="sm">Recommended</AppBadge>}
              {isDefaultTrial && <AppBadge color="emerald" size="sm">Default Trial</AppBadge>}
              {isFree && <AppBadge color="violet" size="sm">Free Plan</AppBadge>}
              {!isPublic && <AppBadge color="amber" size="sm">Private</AppBadge>}
              {plan.isActive === false && <AppBadge color="rose" size="sm">Inactive</AppBadge>}
            </div>
          </div>
          <div className="plan-list-item__pricing-col">
            <span className="plan-list-item__price">{fmt(plan.priceMonthly)}<small>/mo</small></span>
            <span className="plan-list-item__price-alt">{fmt(plan.priceYearly)}<small>/yr</small></span>
          </div>
          <div className="plan-list-item__limits-col">
            {LIMITS.slice(0, 3).map((l) => (
              <span key={l.key} className="plan-list-item__limit">
                {plan[l.key] || "∞"} {l.label}
              </span>
            ))}
          </div>
          <div className="plan-list-item__modules-col">
            <span className="plan-list-item__module-count">
              {enabledCount}/{totalModules} modules
            </span>
          </div>
          <div className="plan-list-item__institutes-col">
            <AppBadge variant="secondary" size="sm">{plan.instituteCount ?? 0} institutes</AppBadge>
          </div>
          <div className="plan-list-item__actions-col">
            <div className="plan-list-item__menu-wrap">
              <button className="plan-list-item__menu-btn" onClick={() => setMenuOpen(!menuOpen)}>
                <MoreVertical size={16} />
              </button>
              {menuOpen && (
                <>
                  <div className="plan-list-item__menu-backdrop" onClick={() => setMenuOpen(false)} />
                  <div className="plan-list-item__menu">
                    <button onClick={() => { onEdit?.(plan); setMenuOpen(false); }}><Edit2 size={14} /> Edit</button>
                    <button onClick={() => { onSetDefaultTrial?.(plan._id); setMenuOpen(false); }}>
                      <Play size={14} /> {isDefaultTrial ? "Remove Trial" : "Set Trial"}
                    </button>
                    <button onClick={() => { onSetRecommended?.(plan._id); setMenuOpen(false); }}>
                      <Star size={14} /> {isPopular ? "Unrecommend" : "Recommend"}
                    </button>
                    <button onClick={() => { onSetFreePlan?.(plan._id); setMenuOpen(false); }}>
                      <Gift size={14} /> {isFree ? "Remove Free" : "Set Free Plan"}
                    </button>
                    <button onClick={() => { onToggleVisibility?.(plan._id, !isPublic); setMenuOpen(false); }}>
                      {isPublic ? <><EyeOff size={14} /> Make Private</> : <><Eye size={14} /> Make Public</>}
                    </button>
                    <button className="plan-list-item__menu-danger" onClick={() => { onDelete?.(plan._id); setMenuOpen(false); }}>
                      <Trash2 size={14} /> Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ── GRID MODE (card) ── */
  return (
    <div className={`plan-card ${isPopular ? "plan-card--popular" : ""} ${!isPublic ? "plan-card--private" : ""}`}>
      {/* Top badges */}
      <div className="plan-card__badges">
        {isPopular && <AppBadge color="blue" size="sm" dot>Recommended</AppBadge>}
        {isDefaultTrial && <AppBadge color="emerald" size="sm" dot>Default Trial</AppBadge>}
        {isFree && <AppBadge color="violet" size="sm" dot>Free Plan</AppBadge>}
        {!isPublic && <AppBadge color="amber" size="sm">Private</AppBadge>}
      </div>

      {/* Header: Name + Pricing */}
      <div className="plan-card__header">
        <h3 className="plan-card__name">{plan.name}</h3>
        <div className="plan-card__price-hero">
          <span className="plan-card__price-amount">{fmt(plan.priceMonthly)}</span>
          {plan.priceMonthly > 0 && <span className="plan-card__price-period">/month</span>}
        </div>
        <div className="plan-card__price-tiers">
          <span>Quarterly: <strong>{fmt(plan.priceQuarterly)}</strong></span>
          <span>Yearly: <strong>{fmt(plan.priceYearly)}</strong></span>
        </div>
      </div>

      {/* Numeric Limits — compact pill grid */}
      <div className="plan-card__limits">
        {LIMITS.map((l) => (
          <div key={l.key} className="plan-card__limit-pill">
            <span className="plan-card__limit-val">{plan[l.key] || "∞"}{l.suffix && plan[l.key] ? l.suffix : ""}</span>
            <span className="plan-card__limit-label">{l.label}</span>
          </div>
        ))}
        {plan.revenueShareType === "flat" && plan.revenueShareFlatAmount > 0 && (
          <div className="plan-card__limit-pill">
            <span className="plan-card__limit-val">{fmt(plan.revenueShareFlatAmount)}</span>
            <span className="plan-card__limit-label">/student Rev. Share</span>
          </div>
        )}
        {(!plan.revenueShareType || plan.revenueShareType === "percentage") && plan.revenueSharePercent > 0 && (
          <div className="plan-card__limit-pill">
            <span className="plan-card__limit-val">{plan.revenueSharePercent}%</span>
            <span className="plan-card__limit-label">Rev. Share</span>
          </div>
        )}
        {plan.extraStoragePricePer100GB > 0 && (
          <div className="plan-card__limit-pill">
            <span className="plan-card__limit-val">{fmt(plan.extraStoragePricePer100GB)}</span>
            <span className="plan-card__limit-label">/100 GB extra</span>
          </div>
        )}
      </div>

      {/* Module summary + expandable details */}
      <div className="plan-card__modules">
        <button className="plan-card__modules-toggle" onClick={() => setExpanded(!expanded)}>
          <span>
            <strong>{enabledCount}</strong> of {totalModules} modules enabled
          </span>
          {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>

        {/* Quick inline check icons when collapsed */}
        {!expanded && (
          <div className="plan-card__modules-quick">
            {MODULE_GROUPS.flatMap((g) =>
              g.keys.filter((k) => plan[k]).map((k) => (
                <span key={k} className="plan-card__module-chip" title={g.labels[k]}>
                  {g.labels[k]}
                </span>
              ))
            )}
          </div>
        )}

        {expanded && (
          <div className="plan-card__modules-detail">
            {MODULE_GROUPS.map((g) => (
              <div key={g.group} className="plan-card__module-group">
                <span className="plan-card__module-group-label">{g.group}</span>
                <div className="plan-card__module-items">
                  {g.keys.map((k) => (
                    <div key={k} className={`plan-card__module-item ${plan[k] ? "plan-card__module-item--on" : ""}`}>
                      {plan[k] ? <Check size={12} /> : <X size={12} />}
                      <span>{g.labels[k]}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Institute count */}
      <div className="plan-card__footer-info">
        <AppBadge variant="secondary" size="sm">{plan.instituteCount ?? 0} institutes</AppBadge>
        {plan.isActive === false && <AppBadge color="rose" size="sm">Inactive</AppBadge>}
      </div>

      {/* Actions */}
      <div className="plan-card__actions">
        <button
          className={`plan-card__act ${isDefaultTrial ? "plan-card__act--trial-on" : ""}`}
          onClick={() => onSetDefaultTrial?.(plan._id)}
          title={isDefaultTrial ? "Remove as Default Trial" : "Set as Default Trial"}
        >
          <Play size={13} />
        </button>
        <button
          className={`plan-card__act ${isFree ? "plan-card__act--free-on" : ""}`}
          onClick={() => onSetFreePlan?.(plan._id)}
          title={isFree ? "Remove as Free Plan" : "Set as Free/Downgrade Plan"}
        >
          <Gift size={13} />
        </button>
        <button
          className={`plan-card__act ${isPopular ? "plan-card__act--star-on" : ""}`}
          onClick={() => onSetRecommended?.(plan._id)}
          title={isPopular ? "Remove Recommended" : "Set as Recommended"}
        >
          <Star size={13} />
        </button>
        <button
          className={`plan-card__act ${!isPublic ? "plan-card__act--private" : ""}`}
          onClick={() => onToggleVisibility?.(plan._id, !isPublic)}
          title={isPublic ? "Make Private" : "Make Public"}
        >
          {isPublic ? <EyeOff size={13} /> : <Eye size={13} />}
        </button>
        <button className="plan-card__act" onClick={() => onEdit?.(plan)} title="Edit">
          <Edit2 size={13} />
        </button>
        <button className="plan-card__act plan-card__act--danger" onClick={() => onDelete?.(plan._id)} title="Delete">
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );
};

export default PlanCard;
